"use client";

import { useLocale } from "next-intl";
import { useApi } from "@lib/helpers/useApi";
import type { generateGetStaticPropsForErrorPage } from "templates/ErrorPage";
import PageContent from "./PageContent";
import { PageTypes } from "@lib/enums/PageTypes";
import { FetchServiceAxios } from "@lib/helpers/fetchServiceAxios";
import { deserialize } from "@ungap/structured-clone";
import { DefaultLayout } from "@app/DefaultLayout";
import { ErrorPageContent } from "@components/errors/ErrorPageContent";
import { SpinnerBox } from "@components/ui/spinner-box/SpinnerBox";
/*
The error.tsx page has to be a client component and as such cannot fetch data on the server.
Instead we fetch it from an API route.
*/
export default function ErrorPage() {
    const locale = useLocale();
    const { data, isLoading, isError } = useApi.useAsync(["error-page-data", locale], () =>
        FetchServiceAxios.get<Awaited<ReturnType<typeof generateGetStaticPropsForErrorPage>>>(
            `/api/error/error-content?locale=${locale}`
        )
    );
    const { serializedSiteSettings, serializedSharedMicrocopySet } = data ?? {};
    const siteSettings = serializedSiteSettings ? deserialize(serializedSiteSettings) : null;
    const sharedMicrocopySet = serializedSharedMicrocopySet
        ? deserialize(serializedSharedMicrocopySet)
        : null;

    if (isLoading) {
        return <SpinnerBox />;
    }

    if (!siteSettings || isError) {
        return <ErrorPageContent />;
    }
    return (
        <PageContent
            pageType={PageTypes.ERROR}
            siteSettings={siteSettings}
            sharedMicrocopySet={sharedMicrocopySet}
        >
            <DefaultLayout siteSettings={siteSettings}>
                <ErrorPageContent />
            </DefaultLayout>
        </PageContent>
    );
}
